// landing/sections/FaqSection.tsx — the skeptic's questions, answered short.
//
// Native <details>/<summary> — accessible, keyboard-operable, zero JS. Each
// answer stays one honest paragraph and links to the page that proves it,
// mirroring the receipts pattern the rest of the landing page uses.

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ConsolePanel } from '@/ui';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled(ConsolePanel).attrs({ as: 'details' })`
  padding: 0;
  overflow: hidden;
`;

const Question = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 14px 16px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};

  &::-webkit-details-marker { display: none; }
  &::before {
    content: '+';
    display: inline-block;
    width: 1.2em;
    font-family: ${({ theme }) => theme.font.mono};
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.color.accentCyan};
  }
  details[open] > &::before { content: '−'; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

const Answer = styled.p`
  margin: 0;
  padding: 0 16px 14px 16px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};

  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: none;
    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
      outline-offset: 2px;
    }
  }
`;

export function FaqSection() {
  return (
    <Wrap aria-labelledby="landing-faq-heading">
      <SectionHeading id="landing-faq-heading">The questions everyone asks</SectionHeading>
      <List>
        <Item>
          <Question>Who actually moderates?</Question>
          <Answer>
            Members do: randomly drawn, rotating juries, never a permanent staff with a
            queue. Every case, vote and written reason is public at{' '}
            <Link to="/council">the Council</Link>.
          </Answer>
        </Item>
        <Item>
          <Question>What can get a post removed?</Question>
          <Answer>
            Exactly five things: direct threats, terrorism, scams, fraud, and coordinated
            abuse. Being wrong, rude, or unpopular is not on the list, and the list does not
            grow quietly. <Link to="/policies">Read the rules</Link>.
          </Answer>
        </Item>
        <Item>
          <Question>Is it really free?</Question>
          <Answer>
            Yes. An account costs nothing and nothing you can do here is paywalled.
            Supporting the platform with an optional upgrade is how the lights stay on.{' '}
            <Link to="/support">That story is public too</Link>.
          </Answer>
        </Item>
        <Item>
          <Question>Do I need the app?</Question>
          <Answer>
            No. The website works on every phone. The Android app is a direct download
            from this site, and iPhone isn&apos;t supported yet.
          </Answer>
        </Item>
        <Item>
          <Question>What about content that is actually illegal?</Question>
          <Answer>
            Content that is unlawful to host is removed directly, without a vote. It is a
            narrow legal floor, not a sixth rule. <Link to="/report-illegal">Reporting it
            needs no account</Link>.
          </Answer>
        </Item>
      </List>
    </Wrap>
  );
}
